import React, { useMemo, useState } from 'react';
import { Button, SparkIcon, Text } from '@spark';
import {
    countChars,
    countWords,
} from '@/features/playground/components/playgroundUtils';

// Same chrome as the generated-response card on Evaluation sample detail:
// hug the content height (no reserved body) so the word-count footer sits
// directly under the text. Do not use Spark CardDefault here — that slot has
// a ~307px min-height which recreates the empty gap this card exists to avoid.
const CARD_STYLE = {
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    background: '#fff',
    height: 'auto',
    alignSelf: 'start',
    minWidth: 0,
};
const SUBTITLE_STYLE = { color: '#8c8c8c', fontSize: 14, fontWeight: 400 };

// Scrolling text body shared by every "ground truth" or summary card on the
// Evaluation > Summarization detail and the Playground detail. Renders the
// "No data available." italic placeholder for empty input so we don't show an
// empty card that looks broken.
//
// ``children`` is rendered as-is so callers can pass a string OR JSX (e.g.
// ``TaggedSample`` for NER ground truth or ``Tag`` chips for classification).
// ``maxHeight`` is a cap only — never a min-height — so short text does not
// leave a blank region above the footer.
export const ScrollText = ({ children, maxHeight }) => {
    const isEmpty =
        children === null ||
        children === undefined ||
        (typeof children === 'string' && children.trim().length === 0);

    return (
        <div
            className="optune-scrollbar"
            data-testid="sample-text-scroll"
            style={{
                color: '#111827',
                fontSize: 13,
                lineHeight: 1.6,
                marginTop: 5,
                height: 'auto',
                minHeight: 0,
                maxHeight,
                flex: '0 1 auto',
                overflowY: 'auto',
                overflowX: 'hidden',
                paddingRight: 6,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
            }}
        >
            {isEmpty ? (
                <Text variant="base-regular" color="secondary" style={{ fontStyle: 'italic' }}>
                    No data available.
                </Text>
            ) : (
                children
            )}
        </div>
    );
};

// Word + character count strip rendered under every text card. Pulled from
// the summarization detail so the playground reference card and the
// summarization sample detail show identical metadata without duplicating
// the markup.
export const MetaCounts = ({ text }) => {
    const words = useMemo(() => countWords(text), [text]);
    const chars = useMemo(() => countChars(text), [text]);
    return (
        <div style={{ marginTop: 16 }}>
            <div
                style={{
                    height: 1,
                    background: '#e5e7eb',
                    marginTop: '32px',
                    marginBottom: '18px',
                }}
            />

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Text variant="base-regular" color="secondary">
                    Word Count
                </Text>
                <Text variant="base-regular" color="primary">
                    {words} words
                </Text>
            </div>

            <div style={{ height: 4 }} />

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Text variant="base-regular" color="secondary">
                    Character Count
                </Text>
                <Text variant="base-regular" color="primary">
                    {chars} chars
                </Text>
            </div>
        </div>
    );
};

// Single text-block card with optional subtitle, scrollable body, and word /
// character count footer. Originally lived inside SummarizationSampleDetail;
// moved here so the Playground reference response card can render exactly the
// same chrome around different content (text, TaggedSample, label tags).
//
// ``children`` is preferred when callers need to render rich content (NER
// TaggedSample, Tag chips). ``text`` is a convenience for the original
// summarization-style "raw text" use, which still pipes the same string
// through both the body and the meta counts.
const SampleTextBlockCard = ({
    title,
    subtitle,
    text,
    children,
    maxHeight = 360,
    metaText,
    collapsible = false,
    defaultOpen = true,
    hideMetaCounts = false,
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const bodyContent = children !== undefined ? children : text;
    const showBody = !collapsible || open;
    return (
        <div className="fs-unmask" style={CARD_STYLE}>
            <div style={{ padding: '16px 24px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text variant="base-semibold" style={{ fontWeight: 600, fontSize: 16 }}>
                        {title}
                    </Text>
                    {collapsible ? (
                        <Button
                            variant="ghost"
                            size="small"
                            onClick={() => setOpen((v) => !v)}
                            icon={<SparkIcon slug={open ? 'chevron-up' : 'chevron-down'} />}
                        />
                    ) : null}
                </div>
            </div>
            <div style={{ padding: '12px 24px 24px' }}>
                {subtitle ? (
                    <Text variant="base-regular" style={{ display: 'block', ...SUBTITLE_STYLE }}>
                        {subtitle}
                    </Text>
                ) : null}
                {showBody ? <ScrollText maxHeight={maxHeight}>{bodyContent}</ScrollText> : null}
                {!hideMetaCounts && (
                    <MetaCounts text={metaText !== undefined ? metaText : text} />
                )}
            </div>
        </div>
    );
};

export default SampleTextBlockCard;
